/**
 * Schedule Jobs › Copy to company: the same rules as the backend (backend/src/utils/scheduleCopy.ts), so the dialog
 * shows what the copy will do. The backend decides again when it copies.
 */

export interface CopyableJob {
  scheduleid: number;
  name: string;
  endpointkey: string;
  selectedfields: string[];
  condition?: string | null;
  datefield?: string | null;
  cronexpression: string;
  status: string;
  backfillchunkdays?: number | null;
  backfillstartdate?: string | null;
}

/**
 * new: the other company lacks it; same-data: it has a job for that SAP data; same-job: it has exactly this job;
 * duplicate: an earlier job in the list has exactly these settings (the copy makes one job of the two).
 */
export type CopyState = 'new' | 'same-data' | 'same-job' | 'duplicate';

const text = (v: unknown): string => String(v ?? '').trim();
const dateOnly = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? text(v).slice(0, 10) : d.toISOString().slice(0, 10);
};

export function scheduleSignature(s: CopyableJob): string {
  const fields = Array.from(new Set((Array.isArray(s.selectedfields) ? s.selectedfields : []).map(text).filter(Boolean))).sort();
  return JSON.stringify([
    text(s.endpointkey),
    text(s.name).toLowerCase(),
    text(s.cronexpression).replace(/\s+/g, ' '),
    text(s.condition),
    text(s.datefield),
    fields,
    Number(s.backfillchunkdays) > 0 ? Number(s.backfillchunkdays) : 0,
    Number(s.backfillchunkdays) > 0 ? dateOnly(s.backfillstartdate) : '',
  ]);
}

/** Where each source job stands against the other company's jobs. */
export function copyStates(source: CopyableJob[], target: CopyableJob[]): Map<number, CopyState> {
  const same = new Set(target.map(scheduleSignature));
  const data = new Set(target.map((t) => text(t.endpointkey)));
  const seen = new Set<string>();
  const out = new Map<number, CopyState>();
  for (const s of source) {
    const sig = scheduleSignature(s);
    if (same.has(sig)) out.set(s.scheduleid, 'same-job');
    else if (seen.has(sig)) out.set(s.scheduleid, 'duplicate');
    else out.set(s.scheduleid, data.has(text(s.endpointkey)) ? 'same-data' : 'new');
    seen.add(sig);
  }
  return out;
}

/** Ticked when the dialog opens: the jobs the other company lacks. */
export function defaultSelection(states: Map<number, CopyState>): Set<number> {
  return new Set(Array.from(states.entries()).filter(([, st]) => st === 'new').map(([id]) => id));
}

/** One line for the result of a copy. */
export function copyResultMessage(created: number, skipped: number, companyName: string): string {
  const jobs = (n: number) => `${n} job${n === 1 ? '' : 's'}`;
  if (!created) return `Nothing copied to ${companyName}: it already has ${skipped === 1 ? 'that job' : 'those jobs'}`;
  return `Copied ${jobs(created)} to ${companyName}${skipped ? `; ${jobs(skipped)} skipped, already there` : ''}`;
}
